/**
 * Scope 3 Submissions List Component
 *
 * Displays a table of all Scope 3 submissions with:
 * - Filtering by category, status, period
 * - Admin actions: download file, enter result
 * - User actions: view, delete
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Pagination,
  Button,
} from '@mui/material';
import {
  Download,
  Delete,
  Edit,
  Search,
  Refresh,
  CheckCircle,
  HourglassEmpty,
} from '@mui/icons-material';
import { useApp } from '../../contexts/AuthContext';
import {
  scope3SubmissionService,
  Scope3Submission,
  Scope3SubmissionListResponse,
  SCOPE3_CATEGORIES,
} from '../../services/scope3SubmissionService';
import { Scope3AdminResultDialog } from './Scope3AdminResultDialog';

interface Scope3SubmissionsListProps {
  onRefresh?: () => void;
}

export const Scope3SubmissionsList: React.FC<Scope3SubmissionsListProps> = ({
  onRefresh,
}) => {
  const { user } = useApp();
  const isAdmin = user?.role === 'Admin';

  // Data state
  const [submissions, setSubmissions] = useState<Scope3Submission[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [periodFilter, setPeriodFilter] = useState<string>('');

  // Pagination
  const [page, setPage] = useState(1);
  const limit = 10;

  // Admin result dialog
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Scope3Submission | null>(null);

  // Fetch submissions
  const fetchSubmissions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response: Scope3SubmissionListResponse = await scope3SubmissionService.listSubmissions({
        category: categoryFilter || undefined,
        status: statusFilter as 'pending' | 'completed' || undefined,
        reporting_period: periodFilter || undefined,
        limit,
        offset: (page - 1) * limit,
      });

      setSubmissions(response.submissions);
      setTotal(response.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setIsLoading(false);
    }
  }, [categoryFilter, statusFilter, periodFilter, page]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  // Handle admin download file
  const handleDownloadFile = async (submission: Scope3Submission) => {
    try {
      const { url, filename } = await scope3SubmissionService.downloadSubmissionFile(submission.id);

      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  };

  // Handle admin enter result
  const handleEnterResult = (submission: Scope3Submission) => {
    setSelectedSubmission(submission);
    setResultDialogOpen(true);
  };

  // Handle delete
  const handleDelete = async (submission: Scope3Submission) => {
    if (!window.confirm(`Delete submission for ${submission.category_name}?`)) {
      return;
    }

    try {
      await scope3SubmissionService.deleteSubmission(submission.id);
      fetchSubmissions();
      if (onRefresh) onRefresh();
    } catch (err: any) {
      setError(err.message || 'Failed to delete submission');
    }
  };

  // Handle result submitted
  const handleResultSubmitted = () => {
    setResultDialogOpen(false);
    setSelectedSubmission(null);
    fetchSubmissions();
    if (onRefresh) onRefresh();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <Box>
      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => {
              setCategoryFilter(e.target.value as number | '');
              setPage(1);
            }}
            label="Category"
          >
            <MenuItem value="">All Categories</MenuItem>
            {SCOPE3_CATEGORIES.map((cat) => (
              <MenuItem key={cat.number} value={cat.number}>
                {cat.number}. {cat.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            label="Status"
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          label="Period"
          placeholder="e.g., FY2024"
          value={periodFilter}
          onChange={(e) => {
            setPeriodFilter(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 150 }}
        />

        <Button
          startIcon={<Refresh />}
          onClick={fetchSubmissions}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Table */}
      {!isLoading && submissions.length === 0 && (
        <Alert severity="info">
          No submissions found. Upload data files to get started.
        </Alert>
      )}

      {!isLoading && submissions.length > 0 && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'grey.50' }}>
                  <TableCell>Category</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>File</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Result (tCO2e)</TableCell>
                  <TableCell>Uploaded</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((submission) => (
                  <TableRow key={submission.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {submission.category_number}. {submission.category_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{submission.facility_name}</TableCell>
                    <TableCell>{submission.reporting_period}</TableCell>
                    <TableCell>
                      <Tooltip title={`${formatFileSize(submission.file_size_bytes)}`}>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {submission.original_filename}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={submission.status === 'completed' ? <CheckCircle /> : <HourglassEmpty />}
                        label={submission.status}
                        size="small"
                        color={submission.status === 'completed' ? 'success' : 'warning'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {submission.result_tco2e !== null && submission.result_tco2e !== undefined ? (
                        <Typography variant="body2" fontWeight="bold" color="success.main">
                          {submission.result_tco2e.toLocaleString()}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(submission.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {/* Admin: Download file */}
                      {isAdmin && (
                        <Tooltip title="Download File">
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadFile(submission)}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Admin: Enter result (only if pending) */}
                      {isAdmin && submission.status === 'pending' && (
                        <Tooltip title="Enter Result">
                          <IconButton
                            size="small"
                            onClick={() => handleEnterResult(submission)}
                            color="primary"
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete (owner or admin) */}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(submission)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Admin Result Dialog */}
      <Scope3AdminResultDialog
        open={resultDialogOpen}
        submission={selectedSubmission}
        onClose={() => {
          setResultDialogOpen(false);
          setSelectedSubmission(null);
        }}
        onSuccess={handleResultSubmitted}
      />
    </Box>
  );
};
